import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Skeleton,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import AccessGroupPermissionsPanel from "components/accessGroups/AccessGroupPermissionsPanel";
import {
  pickerOrder,
  type GroupRefusal,
} from "components/accessGroups/accessStepModel";
import ReadOnlyTooltip from "components/common/ReadOnlyTooltip";
import type { AccessCatalog, AccessGroup } from "types/interfaces/access";
import StepHeading from "./StepHeading";

interface Props {
  catalog: AccessCatalog | undefined;
  groups: AccessGroup[] | undefined;
  selectedGroupId: number | null;
  onSelectGroup: (id: number) => void;
  canChooseGroup: boolean;
  /** Said over every group while none may be chosen here; null says nothing. */
  lockedHint?: string | null;
  /** Holds the greyed look back until it is known whether a group may be chosen. */
  pending?: boolean;
  /** Why a group may not be given here, by group id; groups left out may be. */
  refusals?: Record<number, GroupRefusal>;
  /** What a refused group says when the pointer rests on it. */
  refusalHint?: (group: AccessGroup) => string;
  /** Said under the list about the group picked. */
  note?: string | null;
  aiAgent: boolean;
  onAiAgentChange: (on: boolean) => void;
  canChangeAiAgent: boolean;
  /** Said over the AI Agent switch while it may not change. */
  aiAgentHint?: string | null;
}

/** Step 2 of the internal user form: the access group and the AI Agent switch on
 * the left, what they add up to on the right. */
export default function UserAccessStep({
  catalog,
  groups,
  selectedGroupId,
  onSelectGroup,
  canChooseGroup,
  lockedHint = null,
  pending = false,
  refusals = {},
  refusalHint,
  note = null,
  aiAgent,
  onAiAgentChange,
  canChangeAiAgent,
  aiAgentHint = null,
}: Props) {
  const selected = groups?.find((g) => g.id === selectedGroupId) ?? null;

  return (
    <Box>
      <StepHeading
        title="User Access"
        subtitle="Set the user's access group. This determines what the user will see and do on the web platform."
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(0, 1fr) minmax(0, 1fr)",
          },
          gap: 3,
          alignItems: "start",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box>
            {!groups ? (
              Array.from({ length: 6 }).map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={44}
                  sx={{ mb: 1 }}
                />
              ))
            ) : (
              <RadioGroup
                aria-label="Access group"
                value={selectedGroupId == null ? "" : String(selectedGroupId)}
                onChange={(e) => onSelectGroup(Number(e.target.value))}
              >
                {pickerOrder(groups).map((group) => {
                  const checked = group.id === selectedGroupId;
                  const refused = canChooseGroup && Boolean(refusals[group.id]);
                  // Nothing here may change: every group looks it, the picked
                  // one too. Otherwise only a group that may not be given.
                  const greyed = canChooseGroup
                    ? refused && !checked
                    : !pending;
                  const hint = !greyed
                    ? null
                    : canChooseGroup
                      ? (refusalHint?.(group) ?? null)
                      : lockedHint;
                  const option = (
                    <Box
                      sx={{
                        mb: 1,
                        px: 1.5,
                        border: "1px solid",
                        borderColor: checked
                          ? greyed
                            ? "grey.400"
                            : "primary.main"
                          : "divider",
                        borderRadius: 1,
                        bgcolor: greyed
                          ? "grey.100"
                          : checked
                            ? "calibrate.primarySoft10"
                            : "transparent",
                      }}
                    >
                      <FormControlLabel
                        value={String(group.id)}
                        control={<Radio size="small" />}
                        disabled={!canChooseGroup || greyed}
                        label={
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: greyed
                                  ? "text.disabled"
                                  : "primary.main",
                              }}
                            >
                              {group.name}
                            </Typography>
                            {group.sub_header && (
                              <Typography
                                variant="caption"
                                color={
                                  greyed ? "text.disabled" : "text.secondary"
                                }
                                component="div"
                                sx={{ lineHeight: 1.4 }}
                              >
                                {group.sub_header}
                              </Typography>
                            )}
                          </Box>
                        }
                        sx={{
                          width: "100%",
                          m: 0,
                          py: 0.5,
                          alignItems: "flex-start",
                          "& .MuiRadio-root": { mt: "-2px" },
                        }}
                      />
                    </Box>
                  );
                  return hint ? (
                    <Tooltip
                      key={group.id}
                      title={hint}
                      placement="top"
                      describeChild
                    >
                      {option}
                    </Tooltip>
                  ) : (
                    <Box key={group.id}>{option}</Box>
                  );
                })}
              </RadioGroup>
            )}
            {groups && note && (
              <Typography
                variant="caption"
                color="text.secondary"
                component="p"
                sx={{ m: 0, lineHeight: 1.5 }}
              >
                {note}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700 }}>AI Agent</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ lineHeight: 1.4 }}
              >
                Toggle this switch to enable the set up for the AI Agent. You
                will need a Chat ID and API Key.
              </Typography>
            </Box>
            <ReadOnlyTooltip
              active={!canChangeAiAgent && Boolean(aiAgentHint)}
              title={aiAgentHint ?? undefined}
            >
              <Switch
                checked={aiAgent}
                disabled={!canChangeAiAgent}
                onChange={(e) => onAiAgentChange(e.target.checked)}
                inputProps={{ "aria-label": "AI Agent" }}
              />
            </ReadOnlyTooltip>
          </Box>
        </Box>
        {catalog && selected ? (
          <AccessGroupPermissionsPanel
            catalog={catalog}
            tiers={selected.tiers}
            subHeader="What this user can see and do."
            appliesTo={selected.applies_to}
            aiAgent={aiAgent}
          />
        ) : (
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "grey.50",
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 18,
                lineHeight: 1,
                color: "primary.main",
              }}
            >
              Access Summary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a group to see what the user will be able to do. Without a
              group the user reaches no page.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
